import { EditorActions, EditorState, NodeApi, NodeId, NodeTree, useEditor } from '@candulabs/craft';
import fromPairs from 'lodash/fromPairs';
import mapKeys from 'lodash/mapKeys';
import mapValues from 'lodash/mapValues';
import { nanoid } from 'nanoid';

import { Flags, useFlags } from 'commons/hooks';
import { BuiltInComponents } from 'craft/constants';
import { CRAFT_ROOT_ID, isRootNode } from 'craft/constants/craft';
import {
  GetAvailableActionsProps,
  getAvailableActions,
} from 'craft/containers/Actions/getAvailableActions';
import { documentToCraft } from 'craft/containers/Craft/format';
import { useSelection } from 'craft/hooks/useSelection';
import {
  Action,
  ContentDocument,
  ContentNode,
  INTERACTION_TYPES,
  Resolver,
  SerializedNodeTree,
  HANDLER_TYPES,
} from 'craft/types';
import { getRandomId } from 'craft/utils';

import { findNodeInsertLocation } from './findNodeInsertLocation';
import { normalizeClonedNode } from './normalizeClonedNode';
import { InsertMode } from './types';

const documentCss = ({ css }: ContentNode) => {
  const newCss = css ? { ...css } : {};
  ['overflow', 'max-height', 'max-width', 'box-sizing'].forEach((key: string) => {
    delete newCss[key];
  });
  return newCss;
};

const changeDocumentToView = (contentDocument: ContentDocument) => ({
  ...contentDocument,
  nodes: {
    ...contentDocument.nodes,
    [contentDocument.rootNodeId]: {
      ...contentDocument.nodes[contentDocument.rootNodeId],
      type: 'View',
      css: documentCss(contentDocument.nodes[contentDocument.rootNodeId]),
    },
  },
});

interface GetValidActions extends GetAvailableActionsProps {
  currentActions: Action[];
  availableFlowStepIds: string[];
}

const getValidActions = ({
  currentActions,
  rootName,
  isInForm,
  isMultistepForm,
  isInFlow,
  withModals,
  withLegacyModals,
  withTours,
  withHotspots,
  availableFlowStepIds,
}: GetValidActions) => {
  const availableActions = getAvailableActions({
    rootName,
    isInForm,
    isMultistepForm,
    isInFlow,
    withModals,
    withLegacyModals,
    withTours,
    withHotspots,
  });
  const actionsIds = availableActions.map((action) => action.id);

  return (
    currentActions
      .filter(
        (action: any) =>
          action?.category === INTERACTION_TYPES.Dismiss ||
          actionsIds.includes(action?.category) ||
          actionsIds.includes(action?.handler?.type) ||
          (actionsIds.includes(INTERACTION_TYPES.Content) &&
            action?.handler?.type === HANDLER_TYPES.CHANGE_CONTENT) ||
          (actionsIds.includes(INTERACTION_TYPES.URL) &&
            action?.handler?.type === HANDLER_TYPES.NAVIGATE),
      )
      // Remove actions that has stepId but is not available in the current flow
      .filter((action: any) => {
        if (!action.handler?.stepId) {
          return true;
        }

        return availableFlowStepIds.includes(action.handler?.stepId);
      })
  );
};

export const useMutations = () => {
  const { getSerializedTree } = useSelection();
  const editor = useEditor();
  const { actions, query } = editor;

  const rootName = query.node(CRAFT_ROOT_ID)?.get()?.data?.name;

  const {
    [Flags.MODALS]: withModals,
    [Flags.TOURS]: withTours,
    [Flags.HOTSPOTS]: withHotspots,
    [Flags.TRIGGER_MODAL_ACTION]: withLegacyModals,
  } = useFlags();

  const handlers = {
    /**
     * Given a non-craft tree (such as a block document or a block template) it adds it into the editor
     */
    insertContentDocument: (
      contentDocument: ContentDocument,
      insertToNodeId: string,
      insertMode: InsertMode = InsertMode.AUTO,
    ) => {
      const nodeTree: SerializedNodeTree = {
        rootNodeId: CRAFT_ROOT_ID,
        nodes: documentToCraft(changeDocumentToView(contentDocument)),
      };
      return handlers.insertSerializedNodeTrees([nodeTree], insertToNodeId, insertMode);
    },

    /**
     * Given a tree and a node to insert next to, it will deep clone the tree and give it new ids
     * and then insert it into the original tree.
     */
    insertSerializedNodeTrees: (
      serializedNodeTrees: SerializedNodeTree[],
      insertToNodeId: string,
      insertMode: InsertMode = InsertMode.AUTO,
    ) => {
      const salt = nanoid(6);
      const trees = serializedNodeTrees.map((tree) => {
        const nodeId = tree.rootNodeId;
        const hasFlow = Object.values(tree.nodes).some(
          (node) => node.name === BuiltInComponents.Flow,
        );
        // create a new tree
        const oldIds = Object.keys(tree.nodes);
        const newIdMaps = fromPairs(oldIds.map((oldId: string) => [oldId, getRandomId()]));
        const getNewId = (oldId: string) => newIdMaps[oldId];
        const newNodes = mapValues(
          mapKeys(tree.nodes, (value, key) => getNewId(key)),
          (value, id) => {
            return query
              .parseSerializedNode({ ...value, nodes: value.nodes?.map(getNewId) })
              .toNode(normalizeClonedNode(id, salt, { updateFlowStepActions: hasFlow }));
          },
        );
        return { rootNodeId: getNewId(nodeId) as NodeId, nodes: newNodes } as NodeTree;
      });

      handlers.insertNodeTrees(trees, insertToNodeId, insertMode);
      return trees;
    },

    insertNodeTrees: (
      trees: NodeTree[],
      insertToNodeId: string,
      insertMode: InsertMode = InsertMode.AUTO,
    ) => {
      const nodeInsertLocation = findNodeInsertLocation(trees, insertToNodeId, insertMode, query);
      if (!nodeInsertLocation) {
        return;
      }

      const { parentNodeId, insertIndex } = nodeInsertLocation;
      handlers.insertNodeTreesAtIndex(trees, parentNodeId, insertIndex);
    },

    insertNodeTreesAtIndex: (trees: NodeTree[], parentNodeId: string, insertIndex: number) => {
      const parent = query.node(parentNodeId);

      if (!parent || !parent.node.data.name) {
        return;
      }
      const resolver = query.getResolver();
      const nodeResolver = resolver[parent.node.data.name] as Resolver | undefined;
      if (nodeResolver?.mutations?.insertNodeTreesAtIndex) {
        nodeResolver?.mutations?.insertNodeTreesAtIndex(
          trees,
          parentNodeId,
          insertIndex,
          editor,
          handlers.insertNodeTreesAtIndex,
        );

        // We need to wait the main tree to be updated
        setTimeout(() => {
          // Check the new tree to see if there's any invalid action
          handlers.checkNodeActions(trees, parentNodeId);
        }, 600);
        return;
      }

      const canMoveIn = parent.node.rules.canMoveIn(
        trees.map((tree: NodeTree) => tree.nodes[tree.rootNodeId]),
        parent.node,
        query.node,
      );
      // Ensure all children are allowed within parent
      const canDrop = trees.reduce((canDropNode, { rootNodeId, nodes }) => {
        if (!canDropNode) {
          return false;
        }

        const rootNode = nodes[rootNodeId];

        return rootNode.rules.canDrop(
          parent.node,
          rootNode,
          (nodeId: string) => new NodeApi(parent.nodes, nodeId),
        );
      }, true);

      if (!canMoveIn || !canDrop) {
        return;
      }

      actions.produceState((_: EditorState, actionsWithoutHistory: EditorActions) => {
        actionsWithoutHistory.resetEvents();

        const newTreeIds = trees.map((tree, i) => {
          const newTree: NodeTree = tree;
          actionsWithoutHistory.addNodeTree(newTree, parentNodeId, insertIndex + i);
          return newTree.rootNodeId;
        });

        actionsWithoutHistory.selectNode(newTreeIds);
      });

      // We need to wait the main tree to be updated
      setTimeout(() => {
        // Check the new tree to see if there's any invalid action
        handlers.checkNodeActions(trees, parentNodeId);
      }, 600);
    },

    checkNodeActions: (trees: NodeTree[], parentNodeId: string) => {
      const rootNode = query.node(CRAFT_ROOT_ID);

      if (!rootNode || !rootNode.node.data.name) {
        return;
      }

      const parentFlowStepId = query
        .node(parentNodeId)
        ?.ancestors(true)
        ?.find((n) => query.node(n)?.get()?.data?.name === BuiltInComponents.FlowStep);

      const parentFlowStep = query.node(parentFlowStepId || parentNodeId);
      const currentFlowStepId = parentFlowStep?.node?.data?.props?.stepId;

      const serializedNodes = query.getSerializedNodes();
      const availableFlowStepIds = Object.keys(serializedNodes).reduce(
        (prev, nodeId: string) =>
          serializedNodes[nodeId]?.props?.stepId &&
          serializedNodes[nodeId]?.props?.stepId !== currentFlowStepId
            ? [...prev, serializedNodes[nodeId]?.props?.stepId]
            : prev,
        [],
      );

      Object.keys(trees[0].nodes || {}).forEach((nodeId: string) => {
        const customActions = trees[0].nodes[nodeId]?.data?.custom?.actions;
        if (customActions?.length) {
          const ancestorFormNodeId = query
            .node(nodeId)
            ?.ancestors(true)
            ?.find((n) => query.node(n)?.get()?.data?.name === BuiltInComponents.Form);

          const ancestorFlowNodeId = query
            .node(nodeId)
            ?.ancestors(true)
            ?.find((n) => query.node(n)?.get()?.data?.name === BuiltInComponents.Flow);

          const newActions = getValidActions({
            rootName,
            currentActions: customActions,
            isInForm: !!ancestorFormNodeId,
            isMultistepForm: !!ancestorFormNodeId,
            isInFlow: !!ancestorFlowNodeId,
            withModals: !!withModals,
            withLegacyModals: !!withLegacyModals,
            withTours: !!withTours,
            withHotspots: !!withHotspots,
            availableFlowStepIds,
          });

          if (newActions.length !== customActions.length) {
            // Remove the actions that is not valid in the current scope
            actions.setCustom([nodeId], () => ({
              ...(trees[0].nodes[nodeId]?.data?.custom || {}),
              actions: newActions,
            }));
          }
        }
      });
    },

    deleteNode: (nodeIds: string[]) => nodeIds.length > 0 && actions.deleteNodes(nodeIds),

    /**
     * Given a `nodeId`, it will duplicate it below the current node
     *
     * Right now, the implementation of `duplicateNode` is pretty naive as it assumes
     * that a node can be pasted everywhere.
     *
     * This is of course not the case and in the future we'll have to add some normazilation or
     * validation to make sure that a node can be copied in the correct place.
     *
     * @param nodeIdSelector the node to duplicate
     * @param nextToNodeId duplicate after this node
     */
    duplicateNode: (nodeIdSelector: string | string[], insertToNodeId: string) => {
      const nodeIds = Array.isArray(nodeIdSelector) ? nodeIdSelector : [nodeIdSelector];

      if (nodeIds.length === 0) {
        return;
      }

      const trees: SerializedNodeTree[] = nodeIds
        .filter((nodeId) => !isRootNode(nodeId))
        .map((nodeId) => getSerializedTree(nodeId));

      handlers.insertSerializedNodeTrees(trees, insertToNodeId);
    },
  };

  return handlers;
};
